import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { keepInCatalog, type SavePlace } from '../../services/catalog';
import { PopUpButton, Spinner } from '../../ui/controls';
import { holdKeyboardForMenu } from '../../ui/keys';
import { Icon, ICON_SIZE } from '../chrome/icons';
import type { CanvasHandle } from '../useEditorCanvas';
import { useWork } from '../work';

export type ArtboardTemplate = NonNullable<ReturnType<CanvasHandle['clipArtboard']>>;

/** The snapshot belongs to the artboard whose menu opened this dialog. */
export function SaveTemplateDialog({ template, returnFocus, onClose, onSaved }: {
  template: ArtboardTemplate;
  returnFocus: HTMLElement | null;
  onClose(): void;
  onSaved(message: string): void;
}) {
  const { context } = useWork();
  const project = context?.project && context.canEdit ? context.project : null;
  const [name, setName] = useState(template.name.slice(0, 120));
  const [place, setPlace] = useState<SavePlace>(project ? 'project' : 'mine');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const nameField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const element = dialog.current!;
    const releaseKeys = holdKeyboardForMenu();
    element.showModal();
    // The closing artboard menu returns focus one frame later.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => { nameField.current?.focus(); nameField.current?.select(); });
    });
    return () => {
      cancelAnimationFrame(frame); element.close(); releaseKeys();
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    };
  }, [returnFocus]);

  const save = async () => {
    const called = name.trim();
    if (!called || pending.current) return;
    pending.current = true;
    setSaving(true);
    setError(null);
    try {
      await keepInCatalog({ kind: 'template', name: called, place,
        ...(place === 'project' && project ? { projectId: project.id } : {}),
        layers: template.layers, width: template.width, height: template.height });
      onSaved(`“${called}” saved to ${place === 'mine' ? 'your templates' : place === 'project' ? `${project?.name ?? 'project'} templates` : 'workspace templates'}.`);
      onClose();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'The template could not be saved. Try again.');
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };

  const close = () => { if (!pending.current) onClose(); };
  return createPortal(<dialog ref={dialog} aria-labelledby="save-template-title" aria-describedby="save-template-scope"
    className="m-auto w-[380px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-32px)] overflow-y-auto rounded-[14px] border border-separator bg-[var(--panel-solid)] p-0 text-label shadow-2xl backdrop:bg-black/35"
    onCancel={event => { event.preventDefault(); close(); }}
    onKeyDown={event => {
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); close(); }
    }}>
    <form onSubmit={event => { event.preventDefault(); void save(); }} aria-busy={saving}>
      <header className="flex items-center justify-between border-b border-separator px-[20px] py-[14px]">
        <h2 id="save-template-title" className="text-[15px] font-semibold">Save artboard as template</h2>
        <button type="button" aria-label="Close save template" disabled={saving} onClick={close} className="grid size-[28px] place-items-center rounded-[6px] text-label-2 hover:bg-fill-2 disabled:opacity-40"><Icon name="close" size={ICON_SIZE.small} /></button>
      </header>
      <div className="grid gap-[18px] p-[20px]">
        <div id="save-template-scope" className="flex items-center gap-[12px] rounded-[9px] bg-fill-1 p-[12px]">
          <Icon name="nav-artboard" size={22} />
          <div className="min-w-0"><p className="truncate text-[13px] font-medium">{template.name}</p><p className="mt-[3px] text-[12px] text-label-2">{Math.round(template.width)} × {Math.round(template.height)} px · All layers included</p></div>
        </div>
        <label className="grid gap-[7px] text-[12px] text-label-2">Template name
          <input ref={nameField} required maxLength={120} value={name} disabled={saving} onChange={event => setName(event.target.value)} className="field-focus h-[36px] w-full rounded-[8px] border border-separator bg-fill-1 px-[10px] text-[13px] text-label outline-none" />
        </label>
        <div className="grid gap-[7px] text-[12px] text-label-2"><span>Save to</span>
          <PopUpButton label="Save template to" value={place} disabled={saving} onChange={setPlace} className="h-[36px] w-full border border-separator" options={[
            ['mine', 'Only me'], ...(project ? [['project', project.name] as const] : []), ['workspace', 'Workspace'],
          ]} />
          <span className="text-[11px]">{place === 'mine' ? 'Only you can use this template.' : place === 'project' ? 'Everyone in this project can use this template.' : 'Everyone in this workspace can use this template.'}</span>
        </div>
        {error && <p role="alert" className="text-[12px] text-danger">{error}</p>}
      </div>
      <footer className="flex justify-end gap-[8px] border-t border-separator px-[20px] py-[14px]">
        <button type="button" onClick={close} disabled={saving} className="h-[34px] rounded-[8px] px-[14px] text-[13px] hover:bg-fill-2 disabled:opacity-40">Cancel</button>
        <button type="submit" disabled={saving || !name.trim()} className="flex h-[34px] items-center justify-center gap-[7px] rounded-[8px] bg-label px-[14px] text-[13px] font-medium text-[var(--panel-solid)] hover:opacity-90 disabled:opacity-40">
          {saving && <Spinner size={13} />}{saving ? 'Saving…' : 'Save template'}
        </button>
      </footer>
    </form>
  </dialog>, document.body);
}
